import Guid from "_shared/guids/guid";
import { ProposalAction } from "work/proposals/__constants/proposal-lifecycle";
import Proposal from "work/proposals/proposal";
import { PageTab } from "work/proposals/_views/proposals-page-tabs";

type ProposalListDestinationContext = {
  /** The list tab the row was opened from, which is where a row with no better answer stays. */
  listTab: PageTab;
  userId: Guid | undefined;
  /** A restricted onboarding session has exactly one proposal and one page for it. */
  isRestrictedOnboarding: boolean;
};

/**
 * Where a row on the proposals list opens: the editor that fits what the proposal offers this user.
 *
 * The decision is read from the proposal's available actions, which is the server's answer to "what
 * may this user do here", so the list does not re-derive party or status:
 *
 * - `Edit` without `Revise` is an ordinary draft, and opens in the draft editor.
 * - `Revise` is the redlining editor. That covers a submitted proposal being negotiated and an
 *   unsubmitted draft that revises a hired work agreement -- the latter is already a set of proposed
 *   changes to agreed terms, so it opens in the same redlining editor the work agreement's Revise
 *   action lands in, never the plain draft editor. It lives under `active` whatever the list tab
 *   and whether or not the draft is still `Submit`-able: the draft editor has no revising mode and
 *   there is no `drafts/revise` route, so sending an unsubmitted revision there (as this once did)
 *   bounced the user back to the drafts list.
 * - `Review` overrides the action: a reviewer is asked to review, not to edit.
 * - Nothing offered opens a read-only view on the tab the row came from.
 */
export function proposalListDestination(proposal: Proposal, context: ProposalListDestinationContext): string {
  if (!proposal.id) throw new Error("A proposal needs an id to be opened.");

  return proposalDestination(
    proposal.id.value,
    (action) => proposal.availableActions[action].some((userId) => userId.isEqualTo(context.userId)),
    context,
  );
}

/**
 * The same decision, for a caller holding the list response rather than a deserialized `Proposal`.
 *
 * The dashboard's queue card names individual proposals off the list JSON, the way the active-work
 * card reads its rows off the work-agreement response: the model is large enough that a record
 * failing to deserialize for a reason the row does not care about would cost the row. It still has
 * to open where the proposals list opens, so the rules live here and only the lookup differs —
 * `offers` answers "is this action offered to the viewer", by whatever ids the caller holds.
 */
export function proposalDestination(
  proposalId: string,
  offers: (action: ProposalAction) => boolean,
  context: Omit<ProposalListDestinationContext, "userId">,
): string {
  if (context.isRestrictedOnboarding) return `/proposals/active/revise/${proposalId}/details`;

  const editable = offers(ProposalAction.Edit);
  const revisable = offers(ProposalAction.Revise);
  const reviewable = offers(ProposalAction.Review);

  let tab: PageTab = context.listTab;
  let action: "view" | "edit" | "revise" | "review" = "view";
  if (editable && !revisable) {
    tab = PageTab.Drafts;
    action = "edit";
  }
  if (revisable) {
    tab = PageTab.Active;
    action = "revise";
  }
  if (reviewable) {
    action = "review";
  }

  return `/proposals/${tab}/${action}/${proposalId}`;
}
